import { messageCatalogs, type AppLocale, type MessageTree } from "@airange/shared/i18n-edu-admin";
import routeConfig from "../app/routes";
import { eduAdminMessages } from "./catalog";

const menuMessageKeyAliases: Record<string, string> = {
  eduDashboard: "menu.eduDashboard",
  eduCourses: "menu.eduCourses",
  eduCourseDetail: "menu.eduCourseDetail",
  eduTeachingPlans: "menu.eduTeachingPlans",
  eduProjects: "menu.eduProjects",
  eduProjectsCourseInstances: "menu.eduProjectsCourseInstances",
  eduProjectsExamResults: "menu.eduProjectsExamResults",
  eduPublicCourses: "menu.eduPublicCourses",
  eduMembers: "menu.eduMembers",
  eduOrgs: "menu.eduOrgs",
};

type RouteLocaleNode = {
  name?: string;
  routes?: RouteLocaleNode[];
};

function buildNestedMenuMessageKeyAliases(
  routes: readonly RouteLocaleNode[],
  parentNames: string[] = [],
): Record<string, string> {
  return routes.reduce<Record<string, string>>((result, route) => {
    const nextParentNames = route.name ? [...parentNames, route.name] : parentNames;
    if (route.name) {
      const sourceKey = menuMessageKeyAliases[route.name];
      if (sourceKey) {
        result[`menu.${nextParentNames.join(".")}`] = sourceKey;
      }
    }
    if (route.routes?.length) {
      Object.assign(result, buildNestedMenuMessageKeyAliases(route.routes, nextParentNames));
    }
    return result;
  }, {});
}

const menuMessageKeys = {
  ...Object.fromEntries(
    Object.entries(menuMessageKeyAliases).map(([menuKey, sourceKey]) => [
      `menu.${menuKey}`,
      sourceKey,
    ]),
  ),
  ...buildNestedMenuMessageKeyAliases(routeConfig as RouteLocaleNode[]),
} as const;

function flattenMessageTree(tree: MessageTree, prefix = ""): Record<string, string> {
  return Object.entries(tree).reduce<Record<string, string>>((result, [key, value]) => {
    const nextPrefix = prefix ? `${prefix}.${key}` : key;
    if (typeof value === "string") {
      result[nextPrefix] = value;
      return result;
    }
    return {
      ...result,
      ...flattenMessageTree(value, nextPrefix),
    };
  }, {});
}

export function buildUmiLocaleMessages(locale: AppLocale) {
  const flatMessages = {
    ...flattenMessageTree(messageCatalogs[locale]),
    ...flattenMessageTree(eduAdminMessages[locale]),
  };
  const menuMessages = Object.fromEntries(
    Object.entries(menuMessageKeys).map(([menuKey, sourceKey]) => [
      menuKey,
      flatMessages[sourceKey] ?? sourceKey,
    ]),
  );
  return {
    ...flatMessages,
    ...menuMessages,
  };
}
